import type {
  PublicResolvedSlot,
  PublicWebsiteBlock,
  PublicWebsiteBlockDataSource,
  StoreWebsiteDocument,
  WebsiteBlockManifest,
  WebsiteInterfaceLocale,
  WebsiteLinkValue,
  WebsiteNewsletterCollection,
  WebsitePageKind,
  WebsiteRichTextDocument,
  WebsiteSlotSchema,
  WebsiteSlotValue,
  WebsiteTemplateManifest,
} from '../../types/website-templates';
import { isAllowedSocialUrl } from './external-url';
import { isRetiredWebsiteSlotSemantic } from './internal';
import { validateSlotValue } from './slots';

export const WEBSITE_MEDIA_REF_PREFIX = 'media:';
export const WEBSITE_TEMPLATE_ASSET_REF_PREFIX = 'template:';

export interface WebsiteResolveContext {
  readonly storeSlug: string;
  readonly newsletterCollectionEnabled?: boolean;
  /** Trusted revision-owned IDs; absent context stays explicitly unbound. */
  readonly categoryBindings?: Readonly<Record<number, string | null>>;
  readonly contact: { readonly whatsapp: string | null; readonly phone: string | null };
  /**
   * Same-store renderability (SQL proves it; the canvas mirror is handed the
   * server's answer). Omitted predicates treat every reference as renderable.
   */
  readonly isRenderable?: {
    readonly media?: (mediaAssetId: string) => boolean;
    readonly category?: (categoryId: string) => boolean;
    readonly product?: (productId: string) => boolean;
    readonly collection?: (collectionRef: string) => boolean;
  };
}

export interface ResolvedWebsitePage {
  readonly chrome: readonly PublicWebsiteBlock[];
  readonly blocks: readonly PublicWebsiteBlock[];
}

/** Safe href for a link value, or null when the target is unavailable. */
export function websiteLinkHref(
  link: WebsiteLinkValue,
  context: WebsiteResolveContext,
): string | null {
  const base = `/s/${context.storeSlug}`;
  switch (link.kind) {
    case 'category':
      return context.isRenderable?.category?.(link.categoryId) === false
        ? null
        : `${base}/c/${link.categoryId.toLowerCase()}`;
    case 'product':
      return context.isRenderable?.product?.(link.productId) === false
        ? null
        : `${base}/p/${link.productId.toLowerCase()}`;
    case 'custom-collection':
      return context.isRenderable?.collection?.(link.collectionRef) === false
        ? null
        : `${base}/collections/custom/${link.collectionRef}`;
    case 'store-page':
      if (link.page === 'home') return base;
      if (link.page === 'shop') return `${base}/shop`;
      if (link.page === 'catalog') return `${base}/categories`;
      if (link.page === 'store-info') return `${base}/about`;
      return `${base}/sales`;
    case 'contact':
      if (link.method === 'whatsapp' && context.contact.whatsapp) {
        return `https://wa.me/${context.contact.whatsapp.replace(/^\+/, '')}`;
      }
      if (link.method === 'phone' && context.contact.phone) return `tel:${context.contact.phone}`;
      // Stores have no public email; an email link never resolves.
      return null;
    case 'external':
      // Epic #228 P5 (PD6): re-checked on read. A stored value a later rule
      // refuses renders inert instead of failing the whole page's DTO.
      return isAllowedSocialUrl(link.url) ? link.url : null;
    default:
      return null;
  }
}

/** Plain default copy → link-free document: one paragraph per non-empty line. */
export function websitePlainTextToRichText(text: string): WebsiteRichTextDocument {
  const children = text
    .split('\n')
    .filter((line) => line.length > 0)
    .map((line) => ({
      type: 'paragraph' as const,
      children: [{ type: 'text' as const, text: line }],
    }));
  return { type: 'doc', version: 1, children };
}

function resolveDefault(
  schema: WebsiteSlotSchema,
  locale: WebsiteInterfaceLocale,
  context: WebsiteResolveContext,
): PublicResolvedSlot | null {
  const defaults = schema.defaults;
  if (defaults.kind === 'localized-text') {
    const text = defaults[locale];
    return schema.type === 'rich-text'
      ? { type: 'rich-text', doc: websitePlainTextToRichText(text) }
      : { type: 'text', text };
  }
  if (defaults.kind === 'template-media') {
    return {
      type: 'media',
      kind: 'image',
      src: `${WEBSITE_TEMPLATE_ASSET_REF_PREFIX}${defaults.assetKey}`,
      posterSrc: null,
      alt: '',
      autoplay: false,
    };
  }
  if (defaults.kind === 'link') {
    const href = websiteLinkHref(defaults.value, context);
    return href ? { type: 'link', href } : null;
  }
  // `auto` refs are picked by the block's data source; `none` is empty.
  return null;
}

function resolveAuthored(
  schema: WebsiteSlotSchema,
  value: WebsiteSlotValue,
  context: WebsiteResolveContext,
): PublicResolvedSlot | null {
  const checked = validateSlotValue(schema, value);
  // Fail closed: an invalid stored value renders as empty, never as raw data.
  if (!checked.ok || 'empty' in checked.value) return null;
  const inner = checked.value.v;

  switch (schema.type) {
    case 'newsletter-collection':
      return { type: 'newsletter-collection', collection: inner as WebsiteNewsletterCollection };
    case 'text':
      return { type: 'text', text: inner as string };
    case 'rich-text':
      return { type: 'rich-text', doc: inner as WebsiteRichTextDocument };
    case 'link': {
      const href = websiteLinkHref(inner as WebsiteLinkValue, context);
      return href ? { type: 'link', href } : null;
    }
    case 'media': {
      const media = inner as Extract<WebsiteSlotValue, { v: { mediaAssetId: string } }>['v'];
      if (context.isRenderable?.media?.(media.mediaAssetId) === false) return null;
      if (media.posterAssetId && context.isRenderable?.media?.(media.posterAssetId) === false) {
        return null;
      }
      return {
        type: 'media',
        kind: media.kind,
        src: `${WEBSITE_MEDIA_REF_PREFIX}${media.mediaAssetId.toLowerCase()}`,
        posterSrc: media.posterAssetId
          ? `${WEBSITE_MEDIA_REF_PREFIX}${media.posterAssetId.toLowerCase()}`
          : null,
        alt: media.alt,
        autoplay: media.kind === 'video' && schema.allowAutoplay === true,
        // Epic #228 P6: verbatim when authored, absent otherwise (exact-key
        // DTO; SQL adds the key only when `v ? 'framing'`).
        ...(media.framing === undefined ? {} : { framing: media.framing }),
      };
    }
    case 'category-ref': {
      const { categoryId } = inner as { categoryId: string };
      return context.isRenderable?.category?.(categoryId) === false
        ? null
        : { type: 'category-ref', ref: categoryId.toLowerCase() };
    }
    case 'product-ref': {
      const { productId } = inner as { productId: string };
      return context.isRenderable?.product?.(productId) === false
        ? null
        : { type: 'product-ref', ref: productId.toLowerCase() };
    }
    case 'custom-collection-ref': {
      const { collectionRef } = inner as { collectionRef: string };
      return context.isRenderable?.collection?.(collectionRef) === false
        ? null
        : { type: 'custom-collection-ref', ref: collectionRef };
    }
    default:
      return null;
  }
}

/**
 * One slot: authored value wins; an authored `empty` stays empty; an absent
 * slot falls back to the template default in the store's interface locale.
 */
export function resolvePublicWebsiteSlot(
  schema: WebsiteSlotSchema,
  value: WebsiteSlotValue | undefined,
  locale: WebsiteInterfaceLocale,
  context: WebsiteResolveContext,
): PublicResolvedSlot | null {
  if (value === undefined) return resolveDefault(schema, locale, context);
  return resolveAuthored(schema, value, context);
}

function resolveBlock(
  block: WebsiteBlockManifest,
  schemaById: ReadonlyMap<string, WebsiteSlotSchema>,
  document: StoreWebsiteDocument,
  context: WebsiteResolveContext,
): PublicWebsiteBlock | null {
  if (block.component === 'home.newsletter' && context.newsletterCollectionEnabled !== true)
    return null;
  let dataSource: PublicWebsiteBlockDataSource | undefined;
  const boundId = (ordinal: number): string | null => {
    const id = context.categoryBindings?.[ordinal] ?? null;
    return id !== null && context.isRenderable?.category?.(id) === false ? null : id;
  };
  if (block.dataSource?.kind === 'category-products') {
    dataSource = { ...block.dataSource, categoryId: boundId(block.dataSource.categoryOrdinal) };
  } else if (block.dataSource?.kind === 'bound-categories') {
    dataSource = {
      ...block.dataSource,
      bindings: Array.from({ length: block.dataSource.limit }, (_, index) => ({
        ordinal: index + 1,
        categoryId: boundId(index + 1),
      })),
    };
  } else dataSource = block.dataSource;
  const slots: Record<string, PublicResolvedSlot> = {};
  for (const slotId of block.slotIds) {
    const schema = schemaById.get(slotId);
    if (!schema) continue;
    const resolved = resolvePublicWebsiteSlot(
      schema,
      document.slots[slotId],
      document.interfaceLocale,
      context,
    );
    if (resolved) slots[slotId] = resolved;
  }

  if (block.component === 'chrome.newsletter') {
    const collection = slots['chrome.newsletter.collection'];
    if (collection?.type !== 'newsletter-collection' || !collection.collection.enabled) return null;
  }

  if (block.component === 'home.mission' && slots['home.mission.body']?.type !== 'rich-text')
    return null;
  if (
    block.component === 'home.community' &&
    !Object.values(slots).some((slot) => slot.type === 'media')
  )
    return null;
  if (
    block.component === 'home.promoBanner' &&
    block.variant === 'beauty-campaign' &&
    !slots['home.promo.heading'] &&
    !slots['home.promo.body']
  )
    return null;

  const isEmpty = block.slotIds.length > 0 && Object.keys(slots).length === 0 && !block.dataSource;
  if (isEmpty && block.emptyBehavior === 'hide-block') return null;

  return {
    id: block.id,
    component: block.component,
    variant: block.variant,
    slots,
    emptyBehavior: block.emptyBehavior,
    ...(dataSource ? { dataSource } : {}),
  };
}

/**
 * The document's slots without retired keys (epic #228 P2), so they resolve
 * as if never authored. TS twin of SQL `website_strip_retired_slots`.
 */
function withoutRetiredSlots(
  manifest: WebsiteTemplateManifest,
  document: StoreWebsiteDocument,
): StoreWebsiteDocument {
  const retired = manifest.slots.filter(
    (slot) =>
      isRetiredWebsiteSlotSemantic(slot.semantic) &&
      Object.prototype.hasOwnProperty.call(document.slots, slot.id),
  );
  if (retired.length === 0) return document;
  const slots = { ...document.slots };
  for (const slot of retired) delete slots[slot.id];
  return { ...document, slots };
}

/**
 * TS mirror of SQL `website_resolve_public_blocks`, used by the canvas and the
 * gallery preview. Media stays an opaque reference; hydrate it separately.
 * Authored values of retired slots are ignored (template default).
 */
export function resolvePublicWebsiteBlocks(
  manifest: WebsiteTemplateManifest,
  document: StoreWebsiteDocument,
  pageKind: WebsitePageKind,
  context: WebsiteResolveContext,
): ResolvedWebsitePage {
  const schemaById = new Map(manifest.slots.map((slot) => [slot.id, slot]));
  const resolvable = withoutRetiredSlots(manifest, document);
  const resolveAll = (blocks: readonly WebsiteBlockManifest[]): PublicWebsiteBlock[] =>
    blocks
      .map((block) => resolveBlock(block, schemaById, resolvable, context))
      .filter((block): block is PublicWebsiteBlock => block !== null);

  return {
    chrome: resolveAll(manifest.chrome),
    blocks: resolveAll(manifest.pages[pageKind].blocks),
  };
}
